/* eslint-disable import/no-mutable-exports */

import { getAnalytics } from 'firebase/analytics';
import { deleteApp, initializeApp } from 'firebase/app';
import { browserLocalPersistence, getAuth, initializeAuth } from 'firebase/auth';
// import Firebase here
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { getRemoteConfig } from 'firebase/remote-config';
import { getStorage } from 'firebase/storage';
import { cloneDeep, includes } from 'lodash';
import { Platform } from 'quasar';
import { REFRESH_INTERVAL } from 'src/utils/RemoteConfig';

import packageInfo from '../../package.json';

/** @typedef {import('firebase/auth').Auth} Auth */
/** @typedef {import('firebase/functions').Functions} Functions */
/** @typedef {import('firebase/app').FirebaseApp} FirebaseApp */
/** @typedef {import('firebase/storage').FirebaseStorage} FirebaseStorage */

/** @type {FirebaseApp} */
let firebaseApp = null;
/** @type {Firestore} */
let db = null;
/** @type {Auth} */
let auth = null;
/** @type {FirebaseStorage} */
let storage = null;
/** @type {Functions} */
let functions = null;
let fbConfig = null;
let remoteConfig = null;
let previousRegion;

const defaultRegion = process.env.ENV_TYPE === 'production' ? 'us' : 'eu';
let firebaseConfig = {};

function shouldEnablePersistence() {
  // Always disable persistence in IOS 12 & 13 due to bug: https://bugs.webkit.org/show_bug.cgi?id=197050
  const isIos = Platform.is.ios || false;
  const versionNumber = Platform.is.versionNumber || 0;
  const forceEnablePersistance = localStorage.getItem('forceEnablePersistance') || false;
  return forceEnablePersistance && !(isIos && [12, 13].includes(versionNumber));
}

function initDb() {
  const enablePersistence = shouldEnablePersistence();
  let firestore;

  if (enablePersistence) {
    firestore = initializeFirestore(firebaseApp, {
      experimentalForceLongPolling: true,
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } else {
    firestore = initializeFirestore(firebaseApp, { experimentalForceLongPolling: true });
  }

  return firestore;
}

function initAuth() {
  if (Platform.is.capacitor && Platform.is.ios) {
    /*
     * @see https://jsmobiledev.com/article/firebase-authentication-capacitor/
     * @see https://firebase.google.com/docs/auth/web/custom-dependencies
     */
    return initializeAuth(firebaseApp, {
      persistence: browserLocalPersistence,
    });
  }

  return getAuth(firebaseApp);
}

async function updateFirebaseConnection(regionParam = null, forceRefresh = false) {
  const acceptableRegions = process.env.ENV_TYPE === 'development' ? ['eu', 'ca'] : ['eu', 'ca', 'us', 'au'];

  const queryParamsIndex = window.location.href.indexOf('?');
  const queryParams = queryParamsIndex !== -1
    ? new URLSearchParams(window.location.href.slice(queryParamsIndex))
    : null;
  const queryParamsRegion = queryParams && acceptableRegions.includes(queryParams.get('region'))
    ? queryParams.get('region')
    : '';

  const region = regionParam || queryParamsRegion || localStorage.getItem('selected_region') || defaultRegion;

  if (region === previousRegion && !forceRefresh) {
    return;
  }

  previousRegion = region;

  if (firebaseApp) {
    try {
      // TODO: remove timeout when the issue is fixed, see - https://github.com/firebase/firebase-js-sdk/issues/7816
      await new Promise((resolve) => {
        deleteApp(firebaseApp);
        setTimeout(resolve, 500);
      });
    } catch (err) {
      if (includes(err.message, 'app/app-deleted')) {
        console.warn(err.message);
      } else {
        console.error(err.message);
      }
    }
  }

  let fbConfigValues = process.env.eu;

  switch (region) {
    case 'ca':
      fbConfigValues = process.env.ca;
      break;
    case 'us':
      fbConfigValues = process.env.us;
      break;
    case 'au':
      fbConfigValues = process.env.au;
      break;
    default:
      fbConfigValues = process.env.eu;
      break;
  }

  fbConfig = cloneDeep(fbConfigValues);

  firebaseConfig = {
    appId: fbConfigValues.FIREBASE_APP_ID,
    apiKey: fbConfigValues.FIREBASE_API_KEY,
    projectId: fbConfigValues.FIREBASE_PROJECT_ID,
    authDomain: fbConfigValues.FIREBASE_AUTH_DOMAIN,
    databaseURL: fbConfigValues.FIREBASE_DB_URL,
    storageBucket: fbConfigValues.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: fbConfigValues.FIREBASE_SENDER_ID,
    measurementId: fbConfigValues.FIREBASE_MEASUREMENT_ID,
  };

  firebaseApp = initializeApp(firebaseConfig);
  db = initDb();
  auth = initAuth();
  storage = getStorage(firebaseApp);
  functions = getFunctions(firebaseApp, fbConfigValues.FUNCTIONS_LOCATION);
  // Initialize Remote Config
  remoteConfig = getRemoteConfig(firebaseApp);
  // Set a minimum fetch interval for development, specifying how frequently Firebase allows data fetches
  remoteConfig.settings.minimumFetchIntervalMillis = REFRESH_INTERVAL;

  if (process.env.ENV_TYPE === 'development' && process.env.MODE !== 'capacitor') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  }

  // Firebase analytics in the native app is initialized natively, so we need to initialize it only in the web app
  if (process.env.MODE !== 'capacitor') {
    getAnalytics(firebaseApp);
  }
}

updateFirebaseConnection();

/**
 * @param {string} name - function name
 * @param {Object} [params]
 * @param {HttpsCallableOptions} [options]
 */
const callFirebaseFunction = (name, params = {}, options = {}) => {
  // We add the appInfo to the callable function to be able to log the platform and version
  // TODO: remove this when the appInfo is supported in more functions
  const functionsSupportingAppInfo = ['findUser', 'checkEmailUsageInUsersAndInvitations', 'generateTwilioToken'];
  const newParams = functionsSupportingAppInfo.includes(name)
    ? { ...params, appInfo: { Platform, version: packageInfo.version } }
    : params;
  return httpsCallable(functions, name, options)(newParams);
};

export default ({ app }) => {
  /**
   * Turn off vue performance measure to improve performance of the app in development mode
   * @see https://vuejs.org/api/application.html#app-config-performance
   */
  // eslint-disable-next-line no-param-reassign
  app.config.performance = false;
  // eslint-disable-next-line no-param-reassign
  app.config.errorHandler = (err, vm, info) => {
    if (process.env.ENV_TYPE !== 'production') {
      console.error('Vue errorHandler', err);
      console.error('Error context:', vm, info);
    }
  };
};

export {
  auth,
  callFirebaseFunction,
  db,
  defaultRegion,
  fbConfig,
  firebaseApp,
  firebaseConfig,
  functions,
  storage,
  remoteConfig,
  updateFirebaseConnection,
};
